<template>
  <div class="sp-tooltip-floating">
    <div
      ref="activator"
      class="sp-tooltip-floating__activator"
      tabindex="0"
      :aria-describedby="tooltipId"
      :aria-expanded="isVisible"
      aria-haspopup="true"
      :aria-disabled="isDisabled"
      @mouseenter="onTriggerEnter"
      @mouseleave="onTriggerLeave"
      @focusin="onTriggerEnter"
      @focusout="close"
      @click="toggle"
    >
      <slot />
    </div>

    <transition name="fade">
      <div
        v-if="isVisible"
        :id="tooltipId"
        ref="popup"
        class="sp-tooltip-floating__popup"
        :class="popupClassModifiers"
        role="tooltip"
        @mouseenter="onTooltipEnter"
        @mouseleave="close"
      >
        <div class="sp-tooltip-floating__popup-content-wrapper">
          <div class="sp-tooltip-floating__popup-content">
            <slot name="content">{{ content }}</slot>
          </div>
          <div class="sp-tooltip-floating__popup-arrow" :class="popupArrowClassModifiers"></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { onKeyStroke, useDebounceFn, useEventListener, useTimeoutFn, useWindowSize } from "@vueuse/core";
import { computed, nextTick, ref } from "vue";
import { randomUUID } from "../../../shared/utils/string";
import { toBoolean, toNumber } from "../../utils/props";

/**
 * Component Props
 *
 * @property {string|undefined} content - Tooltip content as string (if no content slot is used)
 * @property {"top"|"bottom"|"left"|"right"} position - Preferred tooltip position
 * @property {boolean|string} disabled - Whether the tooltip is disabled
 * @property {boolean|string} noWrap - Whether the tooltip text should wrap or be ellipsized
 */
const props = defineProps({
  content: {
    type: String,
    default: undefined,
  },
  position: {
    type: String,
    default: "bottom",
    validator: (val) => ["top", "bottom", "left", "right"].includes(val),
  },
  disabled: {
    type: [Boolean, String],
    default: false,
  },
  noWrap: {
    type: [Boolean, String],
    default: false,
  },

  activatorCursor: {
    type: String,
    default: "pointer",
  },

  popupSpacing: {
    type: [Number, String],
    default: 12,
  },
});

const isVisible = ref(false); // Controls visibility of the tooltip
const activator = ref(null); // Ref to the element that triggers the tooltip
const tooltipId = `tooltip-${randomUUID()}`; // Unique ID for accessibility
const isDisabled = computed(() => toBoolean(props.disabled)); // Tracks disabled state reactively
const popup = ref(null); // Ref to the <div> element acting as popup

// Tooltip close delay timer
const { start: startLeaveTimer, stop: cancelLeaveTimer } = useTimeoutFn(close, 200);

// Viewport dimensions
const { width: vpWidth, height: vpHeight } = useWindowSize();

// Tracks the current placement of the tooltip
const currentPosition = ref(props.position);

/**
 * Show tooltip on mouseenter or focus
 */
function onTriggerEnter() {
  if (isDisabled.value) {
    return;
  }
  open();
}

/**
 * Hide tooltip on mouseleave or blur (with delay)
 */
function onTriggerLeave() {
  startLeaveTimer();
}

/**
 * Keep tooltip open if hovered directly
 */
function onTooltipEnter() {
  cancelLeaveTimer();
}

/**
 * Toggle tooltip visibility
 */
function toggle() {
  if (isDisabled.value) {
    return;
  }
  isVisible.value = !isVisible.value;
  schedulePositionUpdate();
}

/**
 * Show the tooltip and schedule a position update
 */
function open() {
  isVisible.value = true;
  schedulePositionUpdate();
}

/**
 * Hide the tooltip
 */
function close() {
  isVisible.value = false;
}

/**
 * Recalculate and apply the tooltip position relative to the activator
 */
function updatePosition() {
  const rect = activator.value?.getBoundingClientRect();
  if (!rect) {
    return;
  }

  isVisible.value = true;

  requestAnimationFrame(() => updateTooltipPopup(rect));
}

/**
 * Handles the full tooltip popup update
 */
function updateTooltipPopup(rect) {
  const popupEl = popup.value;
  if (!popupEl) return;

  const popupRect = popupEl.getBoundingClientRect();
  const spacing = toNumber(props.popupSpacing, 12);

  updateCurrentPosition(rect, popupRect, spacing);
  applyPopupStyles(popupEl, rect, popupRect, spacing);
}

/**
 * Decide tooltip position and update `currentPosition`
 */
function updateCurrentPosition(rect, popupRect, spacing) {
  // Space available around activator
  const spaceAbove = rect.top;
  const spaceBelow = vpHeight.value - rect.bottom;
  const spaceLeft = rect.left;
  const spaceRight = vpWidth.value - rect.right;

  currentPosition.value = props.position;

  // Determine fallback position if preferred doesn't fit
  const fits = {
    top: spaceAbove >= popupRect.height + spacing,
    bottom: spaceBelow >= popupRect.height + spacing,
    left: spaceLeft >= popupRect.width + spacing,
    right: spaceRight >= popupRect.width + spacing,
  };

  if (!fits[props.position]) {
    const fallbackOrder = ["bottom", "top", "right", "left"];
    const fallback = fallbackOrder.find((pos) => fits[pos]);

    if (fallback) {
      currentPosition.value = fallback;
    } else {
      currentPosition.value = "bottom";
    }
  }
}

/**
 * Apply calculated top/left/transform values to the popup
 */
function applyPopupStyles(el, rect, popupRect, spacing) {
  const rawPosition = calculateRawPosition(currentPosition.value, { rect, popupRect, spacing });
  let { top, left, transform } = rawPosition;

  if (["top", "bottom"].includes(currentPosition.value)) {
    const overflowRight = left + popupRect.width / 2 - vpWidth.value;
    const overflowLeft = popupRect.width / 2 - left;

    // Horizontal overflow correction for top/bottom tooltips
    if (overflowRight > 0) {
      left = vpWidth.value - popupRect.width - spacing;
      transform = "none";
    } else if (overflowLeft > 0) {
      left = spacing;
      transform = "none";
    }
  }

  // Vertical overflow correction for left/right tooltips
  if (["left", "right"].includes(currentPosition.value)) {
    const overflowTop = top - popupRect.height / 2;
    const overflowBottom = top + popupRect.height / 2 - vpHeight.value;

    if (overflowTop < 0) {
      top = spacing;
      transform = "none";
    } else if (overflowBottom > 0) {
      top = vpHeight.value - popupRect.height - spacing;
      transform = "none";
    }
  }

  Object.assign(el.style, {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    visibility: "visible",
    transform,
  });
}

function calculateRawPosition(currentPosition, { rect, popupRect, spacing }) {
  // Position strategies per direction
  let transform = "translateX(-50%)";
  const left = rect.left + rect.width / 2;

  if (currentPosition === "top") {
    return {
      top: rect.top - popupRect.height - spacing,
      left,
      transform,
    };
  }

  if (currentPosition === "bottom") {
    return {
      top: rect.bottom + spacing,
      left,
      transform,
    };
  }

  transform = "translateY(-50%)";
  const top = rect.top + rect.height / 2;

  if (currentPosition === "left") {
    return {
      top,
      left: rect.left - popupRect.width - spacing,
      transform,
    };
  }

  return {
    top,
    left: rect.right + spacing,
    transform,
  };
}

/**
 * Schedule updatePosition with nextTick and animation frame for best stability
 */
async function schedulePositionUpdate() {
  await nextTick();
  requestAnimationFrame(updatePosition);
}

/**
 * Close tooltip when window scrolls
 */
function closeIfVisible() {
  if (isVisible.value) {
    close();
  }
}
const delay = 100;
const { debounced: debouncedCloseIfVisible } = useDebounceFn(closeIfVisible, delay);
const debouncedCloseOnExternalScroll = useDebounceFn((event) => {
  if (popup.value?.contains(event.target)) {
    return;
  }
  closeIfVisible();
}, delay);

// Event bindings
useEventListener(window, "resize", debouncedCloseIfVisible);
useEventListener(window, "scroll", debouncedCloseOnExternalScroll, { capture: true });
onKeyStroke("Escape", closeIfVisible);

/**
 * Computed classes for the popup
 */
const popupClassModifiers = computed(() => ({
  "--no-wrap": toBoolean(props.noWrap),
}));

/**
 * Computed classes for the directional arrow
 */
const popupArrowClassModifiers = computed(() => ({
  [`--popup-position-${currentPosition.value}`]: true,
}));
</script>

<style>
:host {
  display: inline-block;
}
</style>

<style lang="scss" scoped>
.sp-tooltip-floating__activator {
  cursor: v-bind(activatorCursor);
}

/**
  * Tooltip wrapper popup with dynamic positioning. <div> element is used to allow focus management and accessibility.
 */
.sp-tooltip-floating__popup {
  --background-color: var(--sp-comp-tooltip-background, var(--sp-sys-color-surface, white));
  --box-shadow: var(--sp-comp-tooltip-shadow, var(--sp-sys-elevation-1, 0 1px 3px rgba(0, 0, 0, 0.2)));

  --popup-border-width: var(
    --sp-comp-tooltip-border-width,
    0px
  ); // (px) unit is in that case required for calc() reasons

  --popup-border-style: var(--sp-comp-tooltip-border-style, solid);
  --popup-border-color: var(--sp-comp-tooltip-border-color, transparent);
  --popup-border: var(--popup-border-width) var(--popup-border-style) var(--popup-border-color);
  --popup-border-radius: var(--sp-comp-tooltip-border-radius, 0.5rem);

  width: var(--sp-comp-tooltip-width, auto);
  max-width: var(--sp-comp-tooltip-max-width, 18rem);
  background: transparent;
  display: block;
  box-sizing: border-box;
  position: fixed;
  visibility: hidden; // Hidden by default, shown when open
  opacity: 1;
  transition: none;
  z-index: var(--sp-comp-tooltip-z-index, var(--sp-ref-z-index-max));

  /**
  * Disables wrapping inside tooltip (used when content should stay on a single line).
  */
  &.--no-wrap {
    white-space: nowrap;
    width: max-content;
    min-width: var(--sp-comp-tooltip-no-wrap-min-width, 3rem);
    max-width: var(--sp-comp-tooltip-no-wrap-max-width, 100%);
    text-overflow: ellipsis;
    overflow: hidden;
  }

  /**
  * Fade transition for tooltip appearance and disappearance.
  */
  &.fade-enter-active,
  &.fade-leave-active {
    transition: opacity 0.2s ease-in-out;
  }

  &.fade-enter-from,
  &.fade-leave-to {
    opacity: 0;
  }

  .sp-tooltip-floating__popup-content-wrapper {
    box-sizing: border-box;
    box-shadow: var(--box-shadow);
    border: var(--popup-border);
    border-radius: var(--popup-border-radius);
    background-color: var(--background-color);
  }

  /**
  * Main tooltip content container.
  */
  .sp-tooltip-floating__popup-content {
    border-radius: var(--popup-border-radius);
    color: var(--sp-comp-tooltip-text-color, var(--sp-sys-color-on-surface, black));
    font: var(--sp-comp-tooltip-font, var(--sp-sys-typescale-small-short, inherit));
    box-sizing: border-box;
    text-align: var(--sp-comp-tooltip-text-align, center);
    position: relative;
    padding: var(--sp-comp-tooltip-padding, 0.75rem 1rem);
    background: var(--background-color);
    z-index: 2;
  }

  .sp-tooltip-floating__popup-arrow {
    --popup-pointer-size: var(--sp-comp-tooltip-pointer-size, 0.75rem);
    --pointer-position: calc(
      50% - (var(--popup-pointer-size) / 2) - var(--popup-border-width)
    ); // default is top center (popup position bottom)
    --pointer-size: var(--popup-pointer-size);

    position: absolute;
    top: 0;
    left: var(--pointer-position);
    width: 100%;
    z-index: 1;
    box-sizing: border-box;

    &::before {
      background-color: var(--background-color);
      border: var(--popup-border);
      box-shadow: var(--box-shadow);
      content: "";
      position: absolute;
      left: 0;
      height: var(--pointer-size);
      width: var(--pointer-size);
      transform: rotate(-45deg);
      top: calc(var(--pointer-size) / 2 * -1);
      z-index: -1;
    }

    &.--popup-position-top {
      top: calc(100% - (var(--popup-border-width) * 2));
    }

    &.--popup-position-right,
    &.--popup-position-left {
      top: calc(50% - var(--popup-border-width));
    }

    &.--popup-position-left {
      left: calc(100% - (var(--popup-pointer-size)) / 2 - (var(--popup-border-width) * 2));
    }

    &.--popup-position-right {
      left: calc(var(--pointer-size) * -1 / 2);
    }
  }
}
</style>
